import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { APP_FULL_TITLE } from "@/lib/brand";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { LoginPage } from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PillarPage from "@/pages/PillarPage";
import GoalsPage from "@/pages/GoalsPage";
import TodosPage from "@/pages/TodosPage";
import VerifyTwoFactorPage from "@/pages/VerifyTwoFactor";
import AppSettings from "@/pages/AppSettings";
import AssetsLayout from "@/pages/assets/AssetsLayout";
import AssetsSummary from "@/pages/assets/AssetsSummary";
import AssetCategory from "@/pages/assets/AssetCategory";
import AssetsSettings from "@/pages/assets/AssetsSettings";
import HealthLayout from "@/pages/health/HealthLayout";
import HealthDashboard from "@/pages/health/HealthDashboard";
import HealthDevices from "@/pages/health/HealthDevices";
import HealthConnections from "@/pages/health/HealthConnections";
import CalendarPage from "@/pages/CalendarPage";
import ReflectionsTimeline from "@/pages/ReflectionsTimeline";
import TravelLayout from "@/pages/travel/TravelLayout";
import TravelRegistration from "@/pages/travel/TravelRegistration";
import TravelSettings from "@/pages/travel/TravelSettings";
import AdminAI from "@/pages/admin/AdminAI";
import AdminDeviceTypes from "@/pages/admin/AdminDeviceTypes";

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-2fa" element={<VerifyTwoFactorPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/settings" element={<AppSettings />} />
        <Route path="/reflections" element={<ReflectionsTimeline />} />
        <Route path="/admin/ai" element={<AdminAI />} />
        <Route path="/admin/device-types" element={<AdminDeviceTypes />} />
        <Route path="/pillar/:pillarId" element={<PillarPage />} />
        <Route path="/pillar/:pillarId/goals" element={<GoalsPage />} />
        <Route path="/pillar/home/todos" element={<TodosPage />} />
        <Route path="/pillar/family/calendar" element={<CalendarPage pillarId="family" />} />
        <Route path="/pillar/friends/calendar" element={<CalendarPage pillarId="friends" />} />
        <Route path="/pillar/work/travel" element={<TravelLayout />}>
          <Route index element={<TravelRegistration />} />
          <Route path="settings" element={<TravelSettings />} />
        </Route>
        <Route path="/pillar/finance/assets" element={<AssetsLayout />}>
          <Route index element={<AssetsSummary />} />
          <Route path="crypto" element={<AssetCategory type="crypto" />} />
          <Route path="stocks" element={<AssetCategory type="stocks" />} />
          <Route path="commodities" element={<AssetCategory type="commodities" />} />
          <Route path="housing" element={<AssetCategory type="housing" />} />
          <Route path="cash" element={<AssetCategory type="cash" />} />
          <Route path="other" element={<AssetCategory type="other" />} />
          <Route path="settings" element={<AssetsSettings />} />
        </Route>
        <Route path="/pillar/health/my-health" element={<HealthLayout />}>
          <Route index element={<HealthDashboard />} />
          <Route path="devices" element={<HealthDevices />} />
          <Route path="connections" element={<HealthConnections />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = APP_FULL_TITLE;
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
